import { ShopMemberDto } from "@babackapp/private-api-client/types";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { Button } from "@babackapp/ui/components/button";
import { Form, FormField, FormItem } from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import useInviteTeamMember from "@babackapp/private-api-client/client/use-invite-team-member";
import useTranslation from "@babackapp/translations/use-translation";
import { RoleSelect } from "@/components/team-members/role-select.tsx";
import { ALL_WAREHOUSES } from "@/components/team-members/constants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import FormInputContainer from "@babackapp/react-hook-forms-components/form-input-container";

const AddTeamMemberFormSchema = z.object({
  email: z.string().email(),
  role: z.string().optional(),
  storeId: z.string().optional(),
  warehouseId: z.string().optional(),
});

type AddTeamMemberFormValues = z.infer<typeof AddTeamMemberFormSchema>;

export default function AddTeamMemberForm() {
  const { data: shop } = useCurrentShopSuspense();
  const locations = shop?.returnToStore.stores?.filter(
    (location) => location.id != null,
  );
  const t = useTranslation();
  const [roleValue, _] = useState<ShopMemberDto["roleId"] | undefined>("ADMIN");

  const { mutateAsync: inviteTeamMember } = useInviteTeamMember(shop.id);

  const form = useForm<AddTeamMemberFormValues>({
    resolver: zodResolver(AddTeamMemberFormSchema),
    defaultValues: {
      email: "",
      role: roleValue,
      storeId: undefined,
    },
  });

  const onSubmit = async (values: AddTeamMemberFormValues) => {
    const role = (values.role ||
      roleValue ||
      "ADMIN") as ShopMemberDto["roleId"];
    await inviteTeamMember({
      email: values.email,
      roleId: role,
      storeId: role === "STORE" ? values.storeId : undefined,
      warehouseId: role === "LOGISTICIAN" ? values.warehouseId : undefined,
    });
    return form.reset();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormInputContainer
          label={t("settings.team.addACollaboratorEmail.title")}
          description={t("settings.team.addACollaboratorEmail.description")}
        >
          <div className="flex gap-3 mb-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="flex-1 min-w-0">
                  <Input
                    {...field}
                    placeholder={t(
                      "settings.team.addACollaboratorEmail.placeholder",
                    )}
                    type="email"
                  />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="w-[160px] shrink-0">
                  <RoleSelect
                    {...field}
                    value={field.value as ShopMemberDto["roleId"]}
                  />
                </FormItem>
              )}
            />
            {form.getValues().role?.toLowerCase() === "store" && (
              <FormField
                control={form.control}
                name="storeId"
                render={({ field }) => (
                  <FormItem>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-10 w-fit">
                        <SelectValue placeholder="Select a store" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations?.map((location) => (
                          <SelectItem key={location.id} value={location.id!}>
                            {location.address?.companyName || "n/a"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}
            {form.getValues().role?.toLowerCase() === "logistician" && (
              <FormField
                control={form.control}
                name="warehouseId"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      value={field.value || ALL_WAREHOUSES}
                      onValueChange={(value) =>
                        field.onChange(
                          value === ALL_WAREHOUSES ? undefined : value,
                        )
                      }
                    >
                      <SelectTrigger className="h-10 w-fit">
                        <SelectValue placeholder={t("carrier.locations.title")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_WAREHOUSES}>
                          {t("returns.filter.allWarehouses")}
                        </SelectItem>
                        {shop?.locations
                          ?.filter((loc) => loc.id != null)
                          .map((loc) => (
                            <SelectItem key={loc.id} value={loc.id!}>
                              {loc.address?.companyName || loc.id}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}
          </div>
        </FormInputContainer>
        {form.getValues().email.length > 0 && (
          <Button
            size="sm"
            variant="default"
            type="submit"
            disabled={!form.formState.isDirty}
            className="animate-in fade-in-0 slide-in-from-top-1 duration-200 mt-2"
          >
            {t("misc.add")}
          </Button>
        )}
      </form>
    </Form>
  );
}
